import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Component, DestroyRef, EventEmitter, Input, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, V2ButtonComponent } from '@coin/shared/core/ui-storybook';
import { filter } from 'rxjs/operators';
import { ContentLanguage } from '@coin/shared/core/util-enums';
import { ConfirmationDialogConfirm, ConfirmationDialogData } from '@coin/shared/core/util-models';
import { CmsContentItem, CmsItem } from '@coin/shared/cms/util';
import { SelectNewContentComponent } from '../select-new-content/select-new-content.component';
import { MatTabGroup, MatTab, MatTabLabel, MatTabContent } from '@angular/material/tabs';
import { PreviewComponent } from '../preview/preview.component';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelContent
} from '@angular/material/expansion';
import { TextBlockComponent } from '../text-block/text-block.component';
import { ImageSliderComponent } from '../image-slider/image-slider.component';
import { QuoteComponent } from '../quote/quote.component';
import { SpacerComponent } from '../spacer/spacer.component';
import { PictureContainerComponent } from '../picture-container/picture-container.component';
import { CompilationComponent } from '../compilation/compilation.component';
import { NumbersComponent } from '../numbers/numbers.component';
import { TextImageLinkComponent } from '../text-image-link/text-image-link.component';
import { HtmlEditorComponent } from '../../shared/html-editor/html-editor.component';
import { EmailSingleDoubleImageComponent } from '../../emails/email-single-double-image/email-single-double-image.component';
import { NewsHeadlineComponent } from '../news-headline/news-headline.component';
import { DownloadFileComponent } from '../../emails/download-file/download-file.component';
import { CmsButtonComponent } from '../cms-button/cms-button.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-cms-edit-news',
  templateUrl: './cms-edit-news.component.html',
  styleUrls: ['./cms-edit-news.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatTabContent,
    PreviewComponent,
    MatAccordion,
    CdkDropList,
    MatExpansionPanel,
    CdkDrag,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    CdkDragHandle,
    MatExpansionPanelContent,
    TextBlockComponent,
    ImageSliderComponent,
    QuoteComponent,
    SpacerComponent,
    PictureContainerComponent,
    CompilationComponent,
    NumbersComponent,
    TextImageLinkComponent,
    HtmlEditorComponent,
    EmailSingleDoubleImageComponent,
    NewsHeadlineComponent,
    DownloadFileComponent,
    CmsButtonComponent,
    V2ButtonComponent,
    TranslatePipe
  ]
})
export class CmsEditNewsComponent {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @Input() settingsData: { languages: ContentLanguage[] };
  @Input() selectedLang: ContentLanguage;
  @Input() item: CmsContentItem;

  @Output() selectedLangChange = new EventEmitter<string>();

  public tabSelectedIndex = 0;

  public copyFromEnglish(item: CmsContentItem, lang: ContentLanguage): void {
    item[lang] = structuredClone(item.en);
  }

  public addContent(item: CmsItem[]): void {
    this.dialog
      .open<SelectNewContentComponent, { preselection: string[] }, CmsItem>(SelectNewContentComponent, {
        disableClose: true,
        maxWidth: 'calc(50vw - 50px)',
        data: {
          preselection: [
            'image-slider',
            'picture-container',
            'compilation',
            'numbers',
            'spacer',
            'text-block',
            'editor',
            'download-file',
            'headline-with-subheadline',
            'button',
            'single-or-multi-image',
            'text-image-link',
            'line'
          ]
        }
      })
      .afterClosed()
      .pipe(
        filter(newItem => !!newItem),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(newItem => {
        item.push(newItem);
        this.addContent(item);
      });
  }

  public changeOfLangTab(i: number): void {
    if (!this.item[this.settingsData.languages[i]]) {
      this.item[this.settingsData.languages[i]] = { content: [] };
    }
    this.selectedLangChange.emit(this.settingsData.languages[i]);
  }

  public getInfoData(item: CmsItem): string {
    let imgSplit: string[];

    switch (item.type) {
      case 'editor':
        return this.getTextFromHTML(item.content);
      case 'headline-with-subheadline':
        return item.headline;
      case 'cards':
        return `${item.content.length} items`;
      case 'spacer':
        return `${item.height}px`;
      case 'button':
        return item.text;
      case 'picture-container':
        return `${item.pictureContainers.length} items`;
      case 'image-slider':
        return `${item.sliderImages.length} items`;
      case 'text-image-link':
        return item.headline;
      case 'download-file':
        return `${item.content.length} items`;
      case 'single-or-multi-image':
        return `${item.content.length} items`;
      case 'full-width-part-with-image':
        if (item.content.headline) {
          return item.content.headline;
        }
        if (item.content.text) {
          return item.content.text;
        }
        imgSplit = item.content.imageUrl.split('/');
        return imgSplit[imgSplit.length - 1];
    }
    return '';
  }

  public getTextFromHTML(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  public removeContent(list: CmsItem[], index: number): void {
    this.dialog
      .open<ConfirmationDialogComponent, ConfirmationDialogData, ConfirmationDialogConfirm>(
        ConfirmationDialogComponent,
        {
          disableClose: true,
          data: {
            headline: 'general.delete-confirm',
            translate: true,
            msg: 'cms.news.delete-item-question',
            confirmMsg: 'general.btnDelete',
            cancelMsg: 'general.btnCancel'
          }
        }
      )
      .afterClosed()
      .pipe(
        filter(remove => !!remove),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        list.splice(index, 1);
      });
  }

  public drop(event: CdkDragDrop<string[]>, list: CmsItem[]): void {
    moveItemInArray(list, event.previousIndex, event.currentIndex);
  }
}
